import * as yup from 'yup';
import { apiEditMaterial, apiEditTexture, apiGetGlobalTags } from '../../helpers/api';
import {
  getCategoryFields,
  getDescriptionFields,
  getLiveLinksFields,
  getHeightFields,
  getNameFields,
  getRotationFields,
  getWidthFields,
  formSchema,
  getSKUFields,
} from './fieldsConfig';
import { useFilterDropdownOptions } from '../../hooks/useFilterDropdownOptions';
import { MATERIAL_FILTER_TYPE, TEXTURE_FILTER_TYPE } from '../../constants/constants';
import { EditSection } from '../EditSection/EditSection';
import { EditPopupUI } from '../EditPopupUI/EditPopupUI';
import { Form } from 'react-bootstrap';
import { useCallback, useMemo, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { errorToast } from '../../helpers/toastHelper';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { texturesActions } from '../../redux/slicers/texturesSlicer';
import { EditPopupButtonsUI } from '../EditPopupUI/EditPopupButtonsUI';
import { CONVERT_TO_CM, MEASURE_UNIT_LABEL } from '../../config/constant/unityConstants';
import { selectActiveMeasureUnitsName } from '../../redux/slicers/admin/curatorSlicer';
import { convertActiveUnitToCM, convertCMToActiveUnits } from '../../helpers/curatorActions';
import { objectToFormData, parseFloatNaN } from '../../helpers/jsHelper';
import { decimals } from '../../helpers/jsHelper';
import { useUserContext } from '../../contexts/UserContext';
import { getTagsFields } from '../PropDetailsPopup/fieldsConfig';
import { createGlobalTagAction } from '../../api/commonApi';
import { dashboardTabMaterialsActions } from '../../redux/slicers/dashboardTabMaterialSlicer';
import { uploadsPageActions } from '../../redux/slicers/uploadsPageSlicer';

export const EditTexturePopup = ({
  data,
  show,
  onClose,
  onUpdateSuccess = () => {},
  isMaterial = false,
}) => {
  const measureUnits = useSelector(selectActiveMeasureUnitsName);
  const DEFAULT_VALUE = decimals(convertCMToActiveUnits(CONVERT_TO_CM), 2);

  const parseTextureSize = (item) => {
    return decimals(convertCMToActiveUnits(parseFloatNaN(item || DEFAULT_VALUE)), 2);
  };

  const parsedData = useMemo(() => {
    if (!data) return data;

    return {
      ...data,
      width: parseTextureSize(parseFloat(data.width)),
      height: parseTextureSize(parseFloat(data.height)),
      rotation: parseInt(data.rotation),
    };
  }, [data]);

  if (!show || !data?.id) return null;

  return (
    <EditTexturePopupUI
      data={parsedData}
      show={show}
      onClose={onClose}
      onUpdateSuccess={onUpdateSuccess}
      measureUnits={measureUnits}
      isMaterial={isMaterial}
    />
  );
};

export const EditTexturePopupUI = ({
  data,
  show,
  onClose,
  onUpdateSuccess = () => {},
  measureUnits,
  isMaterial,
}) => {
  const { isGuestUser, isEditor } = useUserContext();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const { id, name, thumbnail, rotation, filteroptions, sku_id } = data;

  const _textureFilters = useFilterDropdownOptions({
    filterType: isMaterial ? MATERIAL_FILTER_TYPE : TEXTURE_FILTER_TYPE,
    fetchData: true,
  });

  const getSearchTagOption = (item) => ({
    value: typeof item === 'string' ? item : item.name,
    label: typeof item === 'string' ? item : item.name,
  });

  const memoizedSearch = useCallback(async (search, page) => {
    const response = await apiGetGlobalTags({ search, page });

    return {
      options: response.results.map(getSearchTagOption),
      hasMore: Boolean(response.next_link),
      additional: {
        page: page + 1,
      },
    };
  }, []);

  const loadSearchTagOptions = async (search, loadedOptions, { page }) => {
    return memoizedSearch(search, page);
  };

  const tagsValue = data?.tags?.map(getSearchTagOption);

  const handleCreateTag = async (newTagName) => {
    const val = newTagName.trim();
    const result = await dispatch(createGlobalTagAction({ name: val }));
    if (!result?.data?.id) return;

    const existingTags = getValues('tags');
    const existingValues = existingTags.map((a) => a.value);

    setValue('tags', [...existingValues, result?.data?.name].map(getSearchTagOption));
  };

  // showing product page filters only to the Guest User & user with Editor access ( internal user )
  const textureFilters = useMemo(() => {
    if (isEditor || isGuestUser()) {
      return [
        ..._textureFilters?.filter((a) => !a?.is_product_page_filter),
        ..._textureFilters?.filter((a) => a?.is_product_page_filter),
      ];
    }

    return _textureFilters?.filter((a) => !a?.is_product_page_filter);
  }, [_textureFilters, isGuestUser, isEditor]);

  const getFilterValue = (filterId) => {
    if (!filteroptions || !textureFilters) return [];

    const ids = filteroptions.map((o) => o.id);
    const filter = textureFilters.find((filter) => filter.id === filterId);
    const value = filter.options.filter((category) => ids.includes(category.value));
    return value;
  };

  const onSubmit = async (values) => {
    setLoading(true);
    const updatedData = { filteroptions: [] };

    Object.keys(values).forEach((key) => {
      if (key.startsWith('filter_')) {
        const options = values[key];
        if (options) updatedData.filteroptions.push(...options.map((c) => c.value));
      } else if (key === 'width' || key === 'height') {
        updatedData[key] = convertActiveUnitToCM(values[key], 2);
      } else if (key.startsWith('tag')) {
        updatedData[key] = values[key]?.map(data => data?.label).join(',');
      } else {
        updatedData[key] = values[key];
      }
    });

    let response;
    if (isMaterial) {
      const dataForApi = JSON.parse(JSON.stringify(updatedData));
      delete dataForApi['description'];
      delete dataForApi['width'];
      delete dataForApi['height'];
      delete dataForApi['live_links'];
      delete dataForApi['rotation'];
      delete dataForApi['sku_id'];
      let tagValues = dataForApi.tags;
      let filterOptionValues = dataForApi.filteroptions;
      delete dataForApi['tags'];
      delete dataForApi['filteroptions'];
      tagValues = tagValues.split(',');
      const formData = objectToFormData(dataForApi);
      tagValues.forEach((val) => {
        formData.append('tags', val);
      })
      filterOptionValues.forEach((val) => {
        formData.append('filteroptions', val);
      })
      response = await apiEditMaterial(id, formData).finally(() => setLoading(false));
    } else {
      response = await apiEditTexture(id, updatedData).finally(() => setLoading(false));
    }
    if (response?.data) {
      // onUpdateSuccess?.(response.data);
      if (isMaterial) {
        dispatch(dashboardTabMaterialsActions.update(response.data));
      } else {
        const actions = response?.data?.is_map ? uploadsPageActions : texturesActions;
        dispatch(actions.update(response.data));
      }
      onClose();
    }
  };

  const {
    nameField,
    skuField,
    widthField,
    heightField,
    rotationField,
    descriptionField,
    liveLinksFields,
    defaultValues,
    schema,
    tagsFieldsConfig,
  } = useMemo(() => {
    let schemaObj = {
      ...formSchema,
    };

    const tagsFieldsConfig = getTagsFields(tagsValue, loadSearchTagOptions, handleCreateTag);
    const nameField = getNameFields(data);
    const skuField = getSKUFields(data);
    const widthField = getWidthFields(data);
    const heightField = getHeightFields(data);
    const rotationField = getRotationFields(data);
    const descriptionField = getDescriptionFields(data);
    const liveLinksFields = getLiveLinksFields(data);
    const fields = [
      ...nameField,
      ...skuField,
      ...widthField,
      ...heightField,
      ...rotationField,
      ...descriptionField,
      ...liveLinksFields,
      ...tagsFieldsConfig.fieldsConfig
    ];

    textureFilters.map((filter) => {
      const { fieldsConfig, schema } = getCategoryFields(filter, getFilterValue(filter.id));
      fields.push(...fieldsConfig);
      schemaObj = { ...schemaObj, ...schema };
    });

    schemaObj = { ...schemaObj, ...tagsFieldsConfig.schema };

    const defaultValues = fields.reduce((acc, field) => {
      if (!field.name) return acc;

      acc[field.name] = field.value;
      return acc;
    }, {});

    return {
      nameField,
      skuField,
      widthField,
      heightField,
      rotationField,
      descriptionField,
      liveLinksFields,
      defaultValues,
      schema: yup.object(schemaObj),
      tagsFieldsConfig,
    };
  }, [data, textureFilters, data.tags]);

  const methods = useForm({
    resolver: schema ? yupResolver(schema) : undefined,
    defaultValues,
    mode: 'onChange',
  });

  const {
    handleSubmit,
    getValues,
    setValue,
    formState: { isValid, isDirty, isSubmitting },
  } = methods;

  const onInvalid = () => {
    errorToast('Please fix errors', { toastId: 'TOAST_FIX_ERRORS' });
  };

  const subtitle = ((typeof sku_id === 'string' && sku_id.trim() !== '') ||
    typeof sku_id === 'number') && (
    <label className="fullWidth ellipsis light">
      Sku ID - <span>{sku_id}</span>
    </label>
  );

  return (
    <>
      <EditPopupUI
        showDefaultHeader={true}
        showCustomHeader={false}
        hideThumbnail={true}
        show={show}
        onClose={onClose}
        // loading={loading}
        title={name}
        subtitle={subtitle}
        className="rightSidebar leaveHeader texture-details"
        backdropClassName="hideBackdrop">
        <div className="image-part">
          <img
            src={thumbnail}
            alt="texture_image"
            style={{ transform: `rotate(${parseFloat(rotation)}deg)` }}
          />
        </div>

        {data && (
          <FormProvider {...methods}>
            <Form onSubmit={handleSubmit(onSubmit, onInvalid)} autoComplete="off">
              <EditSection
                title={`${isMaterial ? 'Material' : 'Texture'} name*`}
                fields={nameField}
              />

              {!isMaterial && (
                <>
                  <EditSection title="SKU" fields={skuField} />

                  <EditSection
                    title={`Width (${MEASURE_UNIT_LABEL[measureUnits] || ''}) *`}
                    fields={widthField}
                  />

                  <EditSection
                    title={`Height (${MEASURE_UNIT_LABEL[measureUnits] || ''}) *`}
                    fields={heightField}
                  />

                  <EditSection title="Rotation" fields={rotationField} />

                  {/* For Texture: Adding tag section here so that the tags section comes at the begining of the custom filters */}
                  <EditSection
                    title="Tags"
                    fields={tagsFieldsConfig.fieldsConfig}
                    previewMode="fields"
                  />

                  <EditSection title="Description" fields={descriptionField} />
                </>
              )}

              {textureFilters.map((filter) => {
                const { fieldsConfig, schema } = getCategoryFields(
                  filter,
                  getFilterValue(filter.id)
                );
                if (!filter.is_visible) return null;
                return (
                  <EditSection
                    key={filter.id}
                    title={filter.label_name}
                    schema={schema}
                    fields={fieldsConfig}
                    previewMode="fields"
                  />
                );
              })}

              {/* For Material: Adding tag section here so that the tags section comes at the end of the other options */}
              {isMaterial && (
                <EditSection
                  title="Tags"
                  fields={tagsFieldsConfig.fieldsConfig}
                  previewMode="fields"
                />
              )}

              {!isMaterial && (isEditor || isGuestUser()) ? (
                <>
                  {/* Live links */}
                  <EditSection title="Live Links ( comma separated )" fields={liveLinksFields} />
                </>
              ) : null}

              <EditPopupButtonsUI loading={isSubmitting} onCacel={onClose} />
            </Form>
          </FormProvider>
        )}
      </EditPopupUI>
    </>
  );
};